import { formatDate } from "@/utils/helpers";
import { tcmPlanInitialValues } from "./tcmPlanDummy";

export type TcmPlanRecord = {
  id: string;
  patientId: string;
  patientCode: string;
  patientName: string;
  dischargeDate: string;
  dischargedFrom: string;
  dischargedTo: string;
  // dischargeFacility: string;
  complexity: string;
  // icdCodes: string[];
  planStatus: string;
  telehealthStatus: string;
  telehealthAt: string;
  createdAt: string;
  createdBy: string;
};

const ENUM_LABELS: Record<string, string> = {
  acute_hospital: "Acute Hospital",
  inpatient_psych: "Inpatient Psych",
  snf_part_a: "SNF Part A",
  observation: "Observation",
  partial_hospitalization: "Partial Hospitalization",
  home: "Home",
  domiciliary: "Domiciliary",
  rest_home: "Rest Home",
  alf: "ALF",
  moderate: "Moderate",
  high: "High",
  in_progress: "In Progress",
  non_compliant: "Non Compliant",
  complete: "Complete",
  completed: "Completed",
  pending: "Pending",
  scheduled: "Scheduled",
  cancelled: "Cancelled",
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function text(value: unknown) {
  return String(value ?? "").trim();
}

const PLAN_STATUS_VALUES = new Set(["in_progress", "non_compliant", "complete"]);

/** API field is `status`. Keep only the TCM status values the form can save. */
export function normalizePlanStatus(value: unknown) {
  const raw = text(value).toLowerCase().replace(/[\s-]+/g, "_");
  if (raw === "completed") return "complete";
  if (raw === "noncompliant") return "non_compliant";
  if (raw === "inprogress") return "in_progress";
  return PLAN_STATUS_VALUES.has(raw) ? raw : "";
}

export function labelTcmValue(value: unknown) {
  const raw = text(value);
  if (!raw) return "";
  if (ENUM_LABELS[raw]) return ENUM_LABELS[raw];
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function icdCodesOf(row: Record<string, unknown>) {
  const raw = row.icdCodes ?? row.icdCode;
  if (Array.isArray(raw)) {
    return raw
      .map((entry) => {
        if (entry && typeof entry === "object") {
          const record = entry as Record<string, unknown>;
          return text(record.code ?? record.icdCode ?? record.value);
        }
        return text(entry);
      })
      .filter(Boolean);
  }
  const single = text(raw);
  if (!single) return [];
  return single
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function isMongoId(value: string) {
  return /^[a-f0-9]{24}$/i.test(value);
}

function personFrom(value: unknown) {
  const record = asRecord(value);
  if (!record) {
    const raw = text(value);
    return { id: isMongoId(raw) ? raw : "", code: isMongoId(raw) ? "" : raw, name: "" };
  }
  const name = [record.firstName, record.middleName, record.lastName]
    .map(text)
    .filter(Boolean)
    .join(" ");
  const mongoId = text(record._id);
  const anthroId = text(record.patientId);
  return {
    id: mongoId,
    code: anthroId && !isMongoId(anthroId) ? anthroId : "",
    name: name || text(record.name ?? record.fullName),
  };
}

function mapPlan(raw: unknown): TcmPlanRecord | null {
  const row = asRecord(raw);
  if (!row) return null;
  const id = text(row._id ?? row.id);
  const dischargeDate = text(row.dischargeDate);
  if (!id && !dischargeDate) return null;
  const patient = personFrom(asRecord(row.patient) ? row.patient : row.patientId);
  const planPatientMongo =
    typeof row.patientId === "string" && isMongoId(text(row.patientId)) ? text(row.patientId) : "";
  return {
    id: id || dischargeDate,
    patientId: patient.id || planPatientMongo,
    patientCode: patient.code,
    patientName: patient.name,
    dischargeDate,
    dischargedFrom: text(row.dischargedFrom),
    dischargedTo: text(row.dischargedTo),
    // dischargeFacility: text(row.dischargeFacility ?? row.facilityName),
    complexity: text(row.complexity),
    // icdCodes: icdCodesOf(row),
    planStatus: [row.status, row.planStatus, row.tcmStatus]
      .map(normalizePlanStatus)
      .find(Boolean) || "",
    telehealthStatus: text(row.telehealthStatus),
    telehealthAt: text(row.telehealthAt ?? row.nextTelehealthAt ?? row.scheduledAt),
    createdAt: text(row.createdAt),
    createdBy:
      personFrom(row.createdBy ?? row.createdByUser ?? row.careTakerId).name ||
      text(row.createdByName),
  };
}

function listFrom(value: unknown): unknown[] | null {
  if (Array.isArray(value)) return value;
  const record = asRecord(value);
  if (!record) return null;
  const nested = [record.plans, record.tcmPlans, record.tcmPlan, record.items, record.results].find(
    Array.isArray,
  );
  if (Array.isArray(nested)) return nested;
  if (record._id || record.id || record.dischargeDate) return [record];
  return null;
}

export function extractTcmPlansTotal(payload: unknown) {
  const root = asRecord(payload);
  const data = asRecord(root?.data) ?? root;
  const raw = data?.total ?? asRecord(data?.pagination)?.total ?? asRecord(data?.pagination)?.count;
  const total = Number(raw);
  return Number.isFinite(total) ? total : undefined;
}

export function extractTcmPlans(payload: unknown): TcmPlanRecord[] {
  const root = asRecord(payload);
  const buckets = [payload, root?.data, root?.plans, root?.tcmPlans];
  for (const bucket of buckets) {
    const list = listFrom(bucket);
    if (list) {
      return list.map(mapPlan).filter((plan): plan is TcmPlanRecord => Boolean(plan));
    }
  }
  return [];
}

export function tcmPlanFormValues(plan: TcmPlanRecord) {
  return {
    ...tcmPlanInitialValues,
    dischargeDate: plan.dischargeDate.slice(0, 10),
    dischargedFrom: plan.dischargedFrom,
    dischargedTo: plan.dischargedTo,
    // dischargeFacility: plan.dischargeFacility,
    complexity: plan.complexity,
    // icdCodes: plan.icdCodes,
    planStatus: plan.planStatus,
    telehealthStatus: plan.telehealthStatus || tcmPlanInitialValues.telehealthStatus,
  };
}

export function tcmPlanCardCopy(plan: TcmPlanRecord) {
  const from = [labelTcmValue(plan.dischargedFrom), // plan.dischargeFacility
  ].filter(Boolean).join(" - ");
  return {
    dischargedOn: formatDate(plan.dischargeDate) || "—",
    from: from || "—",
    to: labelTcmValue(plan.dischargedTo) || "—",
    planStatus: labelTcmValue(plan.planStatus) || "—",
    complexity: labelTcmValue(plan.complexity) || "—",
    telehealthStatus: labelTcmValue(plan.telehealthStatus) || "—",
    telehealthAt: plan.telehealthAt,
  };
}

function tcmPlanFields(values: Record<string, unknown>) {
  // const codes = Array.isArray(values.icdCodes)
  //   ? values.icdCodes.map((code) => text(code)).filter(Boolean)
  //   : [];
  return {
    dischargeDate: text(values.dischargeDate),
    dischargedFrom: text(values.dischargedFrom),
    dischargedTo: text(values.dischargedTo),
    // dischargeFacility: text(values.dischargeFacility),
    complexity: text(values.complexity),
    // icdCodes: codes,
    status: normalizePlanStatus(values.planStatus),
  };
}

export function tcmCreatePayload(patientId: string, values: Record<string, unknown>) {
  return {
    patientId,
    ...tcmPlanFields(values),
  };
}

export function tcmUpdatePayload(values: Record<string, unknown>) {
  return tcmPlanFields(values);
}

export function extractTcmPlan(payload: unknown): TcmPlanRecord | null {
  const listed = extractTcmPlans(payload);
  if (listed.length === 1) return listed[0];
  const root = asRecord(payload);
  const data = asRecord(root?.data);
  return (
    mapPlan(data?.tcmPlan) ||
    mapPlan(data?.plan) ||
    mapPlan(root?.tcmPlan) ||
    mapPlan(root?.plan) ||
    listed[0] ||
    null
  );
}
